import React, { useState, useRef, useEffect, useCallback } from 'react';
import { InputBase, Tooltip, Drawer } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import LogoutIcon from '@mui/icons-material/Logout';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useNavigate } from 'react-router-dom';

import { auth } from '../../../firebase';
import './styles.css';
import { ROLE_PRIVILEGES } from '../../../common/consts';
import SideBarDirectory from './SidebarDirectory';

const SIDEBAR_WIDTH = 380;

const OrgSwitcher = ({ organizations, organizationId, switchOrganization, loading }) => {
	const [open, setOpen] = useState(false);
	const [orgSearch, setOrgSearch] = useState('');
	const [focusIndex, setFocusIndex] = useState(-1);
	const wrapperRef = useRef(null);
	const searchInputRef = useRef(null);
	const listRef = useRef(null);

	const currentOrg = organizations.find((o) => o.id === organizationId);

	const filtered = orgSearch.trim()
		? organizations.filter((o) => o.name.toLowerCase().includes(orgSearch.toLowerCase().trim()))
		: organizations;

	const openMenu = () => {
		setOpen(true);
		setOrgSearch('');
		setFocusIndex(-1);
		setTimeout(() => searchInputRef.current?.focus(), 0);
	};

	const closeMenu = () => {
		setOpen(false);
		setOrgSearch('');
		setFocusIndex(-1);
	};

	const selectOrg = (org) => {
		switchOrganization(org.id);
		closeMenu();
	};

	// Click outside
	useEffect(() => {
		if (!open) return;
		const handler = (e) => {
			if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
				closeMenu();
			}
		};
		document.addEventListener('mousedown', handler);
		return () => document.removeEventListener('mousedown', handler);
	}, [open]);

	// Scroll focused item into view
	useEffect(() => {
		if (focusIndex >= 0 && listRef.current) {
			const items = listRef.current.querySelectorAll('[role="option"]');
			items[focusIndex]?.scrollIntoView({ block: 'nearest' });
		}
	}, [focusIndex]);

	const handleKeyDown = useCallback((e) => {
		if (e.key === 'Escape') {
			closeMenu();
			return;
		}
		if (e.key === 'ArrowDown') {
			e.preventDefault();
			setFocusIndex((prev) => (prev < filtered.length - 1 ? prev + 1 : 0));
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			setFocusIndex((prev) => (prev > 0 ? prev - 1 : filtered.length - 1));
		} else if (e.key === 'Enter' && focusIndex >= 0 && filtered[focusIndex]) {
			e.preventDefault();
			selectOrg(filtered[focusIndex]);
		}
	}, [filtered, focusIndex]);

	if (loading) {
		return (
			<div className="sidebar__skeleton-org">
				<div className="sidebar__skeleton-org-avatar sidebar__skeleton" />
				<div className="sidebar__skeleton-org-name sidebar__skeleton" />
			</div>
		);
	}

	if (organizations.length <= 1) {
		return (
			<div className="org-switcher__trigger org-switcher__trigger--static">
				<div className="org-switcher__avatar">
					{(currentOrg?.name || 'N')[0].toUpperCase()}
				</div>
				<span className="org-switcher__name">
					{currentOrg?.name || 'No Organizations'}
				</span>
			</div>
		);
	}

	return (
		<div className="org-switcher" ref={wrapperRef}>
			<button
				className={`org-switcher__trigger ${open ? 'org-switcher__trigger--open' : ''}`}
				onClick={open ? closeMenu : openMenu}
				aria-haspopup="listbox"
				aria-expanded={open}
			>
				<div className="org-switcher__avatar">
					{(currentOrg?.name || 'S')[0].toUpperCase()}
				</div>
				<span className="org-switcher__name">{currentOrg?.name || 'Select'}</span>
				<KeyboardArrowDownIcon
					className={`org-switcher__chevron ${open ? 'org-switcher__chevron--open' : ''}`}
					sx={{ fontSize: 18 }}
				/>
			</button>

			{open && (
				<div className="org-switcher__dropdown" role="dialog" onKeyDown={handleKeyDown}>
					<div className="org-switcher__search-row">
						<SearchIcon sx={{ fontSize: 15, color: 'rgba(255,255,255,0.35)' }} />
						<input
							ref={searchInputRef}
							className="org-switcher__search-input"
							type="text"
							placeholder="Search organizations..."
							value={orgSearch}
							onChange={(e) => {
								setOrgSearch(e.target.value);
								setFocusIndex(-1);
							}}
							autoComplete="off"
						/>
					</div>
					<div className="org-switcher__divider" />
					<div className="org-switcher__list" ref={listRef} role="listbox">
						{filtered.length === 0 ? (
							<div className="org-switcher__empty">No organizations found</div>
						) : (
							filtered.map((org, idx) => {
								const isActive = org.id === organizationId;
								const isFocused = idx === focusIndex;
								return (
									<button
										key={org.id}
										role="option"
										aria-selected={isActive}
										className={[
											'org-switcher__option',
											isActive && 'org-switcher__option--active',
											isFocused && 'org-switcher__option--focused',
										].filter(Boolean).join(' ')}
										onClick={() => selectOrg(org)}
										onMouseEnter={() => setFocusIndex(idx)}
									>
										<div className="org-switcher__option-avatar">
											{org.name[0].toUpperCase()}
										</div>
										<span className="org-switcher__option-name">{org.name}</span>
										{isActive && (
											<CheckIcon sx={{ fontSize: 15, color: '#4d94d7', marginLeft: 'auto', flexShrink: 0 }} />
										)}
									</button>
								);
							})
						)}
					</div>
				</div>
			)}
		</div>
	);
};

const SideBar = ({ loadProject, directories, organizationId, user, organizations, orgsLoading, directoriesLoading, switchOrganization, selectedProjectId, open, onOpenChange }) => {
	const [searchQuery, setSearchQuery] = useState('');
	const hasProject = !!selectedProjectId;

	const navigate = useNavigate();

	const toggleDrawer = () => {
		onOpenChange(!open);
	};

	const logout = () => {
		auth.signOut();
	};

	const onProjectClick = (project, directoryId) => {
		loadProject(project, directoryId);
		onOpenChange(false);
	};

	const sidebarContent = (
		<div className="sidebar">
			{/* Header with org switcher */}
			<div className="sidebar__top">
				<div className="sidebar__org-section">
					<span className="sidebar__org-label">Organization</span>
					<div className="sidebar__org-row">
						<OrgSwitcher
							organizations={organizations}
							organizationId={organizationId}
							switchOrganization={switchOrganization}
							loading={orgsLoading}
						/>
						{(user?.privilegeLevel >= ROLE_PRIVILEGES.ADMIN || hasProject) && (
							<div className="sidebar__top-actions">
								{user?.privilegeLevel >= ROLE_PRIVILEGES.ADMIN && (
									<Tooltip title="Admin panel" placement="bottom" arrow>
										<button
											className="sidebar__action-btn"
											onClick={() => navigate('/admin/organizations')}
										>
											<AdminPanelSettingsOutlinedIcon sx={{ fontSize: 17 }} />
										</button>
									</Tooltip>
								)}
								{hasProject && (
									<Tooltip title="Close panel" placement="bottom" arrow>
										<button className="sidebar__action-btn" onClick={toggleDrawer}>
											<ChevronLeftIcon sx={{ fontSize: 18 }} />
										</button>
									</Tooltip>
								)}
							</div>
						)}
					</div>
				</div>
			</div>

			{/* Search */}
			<div className="sidebar__search">
				<SearchIcon sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 18 }} />
				<InputBase
						placeholder="Search projects..."
					value={searchQuery}
					onChange={(e) => setSearchQuery(e.target.value)}
					sx={{
						color: 'rgba(255,255,255,0.88)',
						flex: 1,
						fontSize: '13.5px',
						'& ::placeholder': {
							color: 'rgba(255,255,255,0.45)',
							opacity: 1,
						},
					}}
				/>
				{searchQuery && (
					<IconButton size="small" onClick={() => setSearchQuery('')} sx={{ padding: '2px' }}>
						<ClearIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 16 }} />
					</IconButton>
				)}
			</div>

			{/* Scrollable tree */}
			<div className="sidebar__tree">
					<span className="sidebar__section-label">Projects</span>
				{(orgsLoading || directoriesLoading) ? (
					<div className="sidebar__skeleton-rows">
						{[100, 75, 88].map((w) => (
							<div key={w} className="sidebar__skeleton-row">
								<div className="sidebar__skeleton-row-icon sidebar__skeleton" />
								<div className="sidebar__skeleton-row-label sidebar__skeleton" style={{ width: `${w}%` }} />
							</div>
						))}
					</div>
				) : (
					directories.map((directory) => (
						<SideBarDirectory
							key={directory.id}
							onProjectClick={onProjectClick}
							directory={directory}
							organizationId={organizationId}
							searchQuery={searchQuery}
							selectedProjectId={selectedProjectId}
						/>
					))
				)}
			</div>

			{/* Footer */}
			<div className="sidebar__footer">
				<button className="sidebar__logout-btn" onClick={logout}>
					<LogoutIcon sx={{ fontSize: 15 }} />
					<span>Log out</span>
				</button>
			</div>
		</div>
	);

	return (
		<>
			<Drawer
				variant="persistent"
				anchor="left"
				open={open}
				transitionDuration={250}
				PaperProps={{
					sx: {
						width: SIDEBAR_WIDTH,
						backgroundColor: '#0f2d4e',
						borderRight: 'none',
					},
				}}
			>
				{sidebarContent}
			</Drawer>
			{!open && (
				<div className="sidebar__closed-strip">
					<IconButton onClick={toggleDrawer} className="sidebar__menu-icon">
						<MenuIcon sx={{ color: '#fff', fontSize: 20 }} />
					</IconButton>
				</div>
			)}
		</>
	);
};

export { SIDEBAR_WIDTH };
export default SideBar;
